// Chart › Finance › Budget vs actual (`/c/{slug}/chart/finance`; 11 §Planning "Budget vs actual",
// M5-03 `finance.budgetVsActual`; Chart UX pass 2026-10-21, Mosaic / Runway / Brex budgets): four
// compact KPI tiles over a variance table of the company → team → seat tree for a month, quarter
// or year. Each row: budget, actual, variance, the budget used as an inline bar, and what the
// actual is made of; team rows collapse their seats; a seat row shows the person holding it and
// opens the seat page. Pay (compensation, benefits) is computed by the API on read; a seat whose
// pay the caller may not read comes without it (`restricted_hidden`, "Pay hidden"), while team
// and company totals keep it. One call on load (the view); the names are the org chart's, which
// the sidebar already holds.
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  EmptyState,
  InlineBar,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  Building2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  EyeOffIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { SeatAvatar } from "../chart/chart-parts";
import type { CompanyAccess } from "../company";
import { monthOfWeek } from "../de/cost-tab";
import { formatMoney } from "../format";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import {
  FINANCE_KIND_LABELS,
  FinancePage,
  monthsAround,
  PayHiddenBadge,
  periodLabel,
  ratioText,
  useOrgNames,
} from "./finance-parts";
import { budgetVsActualQuery, type PeriodType } from "./finance-queries";

type Node = Awaited<ReturnType<Api["client"]["finance"]["budgetVsActual"]>>["nodes"][number];

const INDENT = ["", "pl-5", "pl-10", "pl-15", "pl-20", "pl-25"] as const;

/** The period keys offered per type, newest first, around the focused month. */
export function periodOptions(type: PeriodType, month: string): string[] {
  const y = Number(month.slice(0, 4));
  if (type === "month") return monthsAround(month, 12, 1);
  if (type === "year") return [y + 1, y, y - 1, y - 2].map(String);
  const q = Math.ceil(Number(month.slice(5, 7)) / 3);
  return Array.from({ length: 8 }, (_, i) => {
    const n = y * 4 + (q - 1) + 1 - i;
    return `${Math.floor(n / 4)}-Q${(n % 4) + 1}`;
  });
}

/** The tree in display order: each node, then its children (teams before seats, by name). */
export function treeOrder(nodes: readonly Node[]): { node: Node; depth: number }[] {
  const kids = new Map<string | null, Node[]>();
  for (const n of nodes) kids.set(n.parent_id, [...(kids.get(n.parent_id) ?? []), n]);
  const known = new Set(nodes.map((n) => n.id));
  // Roots: the company, or (for a seat holder) the nodes whose parent is not visible.
  const roots = nodes.filter((n) => n.parent_id === null || !known.has(n.parent_id));
  const rank = { company: 0, team: 1, seat: 2 } as const;
  const sorted = (list: Node[]) =>
    [...list].sort((a, b) => rank[a.kind] - rank[b.kind] || a.name.localeCompare(b.name));
  const out: { node: Node; depth: number }[] = [];
  const walk = (n: Node, depth: number) => {
    out.push({ node: n, depth });
    for (const c of sorted(kids.get(n.id) ?? [])) walk(c, depth + 1);
  };
  for (const r of sorted(roots)) walk(r, 0);
  return out;
}

/** "Over budget" / "Near" (≥ 80 %) / "Within" / "No budget". */
export function budgetState(n: Pick<Node, "budget_minor" | "utilization">) {
  if (n.budget_minor === null) return { label: "No budget", tone: "neutral" as const };
  const u = n.utilization === null ? null : Number(n.utilization);
  if (u !== null && u > 1) return { label: "Over budget", tone: "danger" as const };
  if (u !== null && u >= 0.8) return { label: "Near budget", tone: "warning" as const };
  return { label: "Within budget", tone: "success" as const };
}

const BAR_TONE = {
  danger: "danger",
  warning: "warning",
  success: "primary",
  neutral: "neutral",
} as const;

/** A 6 px inline bar of `ratio` (a decimal string), capped at 100 %, in the state's tone. */
export function UsedBar({
  ratio,
  tone,
  label,
}: {
  ratio: string | null;
  tone: keyof typeof BAR_TONE;
  label: string;
}) {
  if (ratio === null) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="flex min-w-0 items-center gap-2" data-slot="used-bar">
      <InlineBar
        role="img"
        aria-label={label}
        value={Number(ratio) * 100}
        tone={BAR_TONE[tone]}
        className="min-w-16 flex-1"
      />
      <span
        className={cn(
          "w-12 shrink-0 text-right text-xs tabular-nums",
          tone === "danger"
            ? "text-danger-text"
            : tone === "warning"
              ? "text-warning-text"
              : "text-muted-foreground",
        )}
      >
        {ratioText(ratio)}
      </span>
    </span>
  );
}

export function BudgetVsActualScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const focusMonth = monthOfWeek(usePeriod());
  const [type, setType] = useState<PeriodType>("month");
  const options = useMemo(() => periodOptions(type, focusMonth), [type, focusMonth]);
  const [picked, setPicked] = useState<Record<PeriodType, string | null>>({
    month: null,
    quarter: null,
    year: null,
  });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const key = picked[type] ?? (type === "month" ? focusMonth : (options[1] ?? options[0] ?? ""));
  const view = useQuery(budgetVsActualQuery(api, company.id, type, key));
  const names = useOrgNames(api, company);
  const nodes = view.data?.nodes ?? [];
  const currency = view.data?.currency ?? "USD";
  const root = nodes.find((n) => n.kind === "company");
  const hidden = nodes.filter((n) => n.restricted_hidden).length;
  const money = (minor: number | null) => (minor === null ? "—" : formatMoney(minor, currency));
  const parentOf = new Map(nodes.map((n) => [n.id, n.parent_id]));
  const hiddenUnder = (id: string) => {
    let cur = parentOf.get(id) ?? null;
    while (cur) {
      if (collapsed.has(cur)) return true;
      cur = parentOf.get(cur) ?? null;
    }
    return false;
  };
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const ordered = treeOrder(nodes);
  const hasKids = new Set(nodes.flatMap((n) => (n.parent_id ? [n.parent_id] : [])));
  const seats = nodes.filter((n) => n.kind === "seat").length;

  return (
    <FinancePage
      company={company}
      where="chart"
      title="Budget vs actual"
      icon={WalletIcon}
      screen="finance-bva"
      {...(view.data ? { count: seats } : {})}
      actions={
        <Link
          to="/c/$slug/kpis/reports/finance/exports"
          params={{ slug: company.slug }}
          search={keepFocus}
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
        >
          <DownloadIcon aria-hidden="true" />
          Export
        </Link>
      }
      toolbar={
        <>
          <Segmented
            aria-label="Period type"
            size="sm"
            value={type}
            onValueChange={setType}
            options={[
              { value: "month", label: "Month" },
              { value: "quarter", label: "Quarter" },
              { value: "year", label: "Year" },
            ]}
          />
          <NativeSelect
            aria-label="Period"
            value={key}
            onChange={(e) => setPicked((p) => ({ ...p, [type]: e.target.value }))}
            className="h-7 w-auto text-xs"
          >
            {options.map((k) => (
              <NativeSelectOption key={k} value={k}>
                {periodLabel(k)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <span className="text-xs text-muted-foreground tabular-nums">{currency}</span>
          {hidden > 0 ? (
            <span
              className="ml-auto flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
              data-slot="pay-hidden-notice"
            >
              <EyeOffIcon aria-hidden="true" className="size-3.5" />
              Pay left out of {hidden === 1 ? "1 seat" : `${hidden} seats`} you may not read. Team
              and company totals include them.
            </span>
          ) : null}
        </>
      }
    >
      {view.isPending ? (
        <Loading label="Loading budget vs actual" rows={6} />
      ) : view.isError ? (
        <LoadError what="Budget vs actual" error={view.error} />
      ) : nodes.length === 0 ? (
        <EmptyState
          variant="card"
          icon={WalletIcon}
          title="Nothing to show"
          description="No seat or team of yours is in this company's tree."
        />
      ) : (
        <>
          {root ? (
            <StatGrid dense data-slot="bva-totals" aria-label={`${periodLabel(key)} totals`}>
              <StatTile
                size="sm"
                label="Actual"
                value={money(root.actual_minor)}
                detail={periodLabel(key)}
              />
              <StatTile
                size="sm"
                label="Budget"
                value={money(root.budget_minor)}
                detail={root.budget_minor === null ? "No company budget" : "Company budget"}
              />
              <StatTile
                size="sm"
                label={(root.variance_minor ?? 0) < 0 ? "Over budget by" : "Left in budget"}
                value={root.variance_minor === null ? "—" : money(Math.abs(root.variance_minor))}
                detail={budgetState(root).label}
              />
              <StatTile
                size="sm"
                label="Budget used"
                value={ratioText(root.utilization)}
                detail={
                  <UsedBar
                    ratio={root.utilization}
                    tone={budgetState(root).tone}
                    label={`${ratioText(root.utilization)} of the company budget used`}
                  />
                }
              />
            </StatGrid>
          ) : null}
          <Table data-slot="bva-tree">
            <TableCaption className="sr-only">
              {periodLabel(key)} in {currency}. Variance is budget minus actual.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Team or seat</TableHead>
                <TableHead scope="col" className="text-right">
                  Budget
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Actual
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Variance
                </TableHead>
                <TableHead scope="col" className="w-48 max-md:hidden">
                  Budget used
                </TableHead>
                <TableHead scope="col" className="max-lg:hidden">
                  Made of
                </TableHead>
                <TableHead scope="col" className="w-10">
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordered.map(({ node: n, depth }) => {
                if (hiddenUnder(n.id)) return null;
                const state = budgetState(n);
                const kinds = Object.entries(n.by_kind)
                  .filter(([, v]) => v !== 0)
                  .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
                const seat = n.kind === "seat" ? names.seatOf(n.id) : undefined;
                const open = !collapsed.has(n.id);
                return (
                  <TableRow
                    key={n.id}
                    data-node={n.id}
                    data-kind={n.kind}
                    className={cn("group/row h-9", n.kind === "company" && "bg-sunken")}
                  >
                    <TableHead
                      scope="row"
                      className={cn(
                        "py-1 font-normal",
                        INDENT[Math.min(depth, INDENT.length - 1)],
                        n.kind !== "seat" && "font-medium",
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {hasKids.has(n.id) ? (
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-expanded={open}
                            aria-label={`${open ? "Collapse" : "Expand"} ${n.name}`}
                            onClick={() => toggle(n.id)}
                            className="-ml-1 text-faint"
                          >
                            {open ? (
                              <ChevronDownIcon aria-hidden="true" />
                            ) : (
                              <ChevronRightIcon aria-hidden="true" />
                            )}
                          </Button>
                        ) : n.kind !== "seat" ? (
                          <span className="inline-block size-6" aria-hidden="true" />
                        ) : null}
                        {n.kind === "company" ? (
                          <Building2Icon aria-hidden="true" className="size-4 text-faint" />
                        ) : n.kind === "team" ? (
                          <UsersIcon aria-hidden="true" className="size-4 text-faint" />
                        ) : seat ? (
                          <SeatAvatar seat={seat} size="sm" />
                        ) : null}
                        {n.kind === "seat" ? (
                          <Link
                            to="/c/$slug/kpis/chart/seat/$id"
                            params={{ slug: company.slug, id: n.id }}
                            search={keepFocus}
                            className="flex min-w-0 items-baseline gap-1.5 underline-offset-4 hover:underline"
                          >
                            {seat?.holders.length ? (
                              <>
                                <span className="truncate font-medium">
                                  {seat.holders.map((h) => h.display_name).join(", ")}
                                </span>
                                <span className="truncate text-xs text-muted-foreground">
                                  {n.name}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="truncate">{n.name}</span>
                                {seat ? (
                                  <span className="text-xs text-muted-foreground">open</span>
                                ) : null}
                              </>
                            )}
                          </Link>
                        ) : (
                          <span className="truncate">{n.name}</span>
                        )}
                        {n.restricted_hidden ? <PayHiddenBadge /> : null}
                      </span>
                    </TableHead>
                    <TableCell className="py-1 text-right tabular-nums">
                      {money(n.budget_minor)}
                    </TableCell>
                    <TableCell className="py-1 text-right tabular-nums">
                      {money(n.actual_minor)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "py-1 text-right tabular-nums max-md:hidden",
                        (n.variance_minor ?? 0) < 0 && "text-danger-text",
                      )}
                    >
                      {n.variance_minor === null
                        ? "—"
                        : `${n.variance_minor < 0 ? "−" : ""}${money(Math.abs(n.variance_minor))}`}
                    </TableCell>
                    <TableCell className="py-1 max-md:hidden">
                      {n.utilization === null ? (
                        <Badge variant={state.tone}>{state.label}</Badge>
                      ) : (
                        <UsedBar
                          ratio={n.utilization}
                          tone={state.tone}
                          label={`${n.name}: ${ratioText(n.utilization)} of the budget used, ${state.label.toLowerCase()}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="max-w-72 truncate py-1 text-xs text-muted-foreground max-lg:hidden">
                      {kinds.length
                        ? kinds
                            .slice(0, 3)
                            .map(([k, v]) => `${FINANCE_KIND_LABELS[k] ?? k} ${money(v)}`)
                            .join(" · ")
                        : "No spend"}
                    </TableCell>
                    <TableCell className="py-1">
                      {n.kind === "seat" ? (
                        <Link
                          to="/c/$slug/kpis/chart/seat/$id"
                          params={{ slug: company.slug, id: n.id }}
                          search={keepFocus}
                          aria-label={`Open ${n.name}`}
                          title="Open seat"
                          className={cn(
                            buttonVariants({ variant: "ghost", size: "icon-xs" }),
                            "opacity-0 transition-opacity duration-(--dur-fast) group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100",
                          )}
                        >
                          <ArrowUpRightIcon aria-hidden="true" />
                        </Link>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </FinancePage>
  );
}
